import type { ComponentType, SVGProps } from 'react';
import { Button } from './Button';

// OptionCard (P10 §2.4; story 2-146a) — a selectable icon+title+description
// card for a ONE-SHOT choice among a small set of named paths (e.g. "how do
// you want to create this template"), as distinct from a toggleable list
// that stays on screen carrying a selected/unselected state (RadioGroup, or
// the starter-picker's own Button-as-card list in
// `modules/templates/components/CreateTemplateDialog.tsx`, which keeps that
// shape deliberately — see this file's own extraction note in that
// component). Picking an OptionCard is a navigation: it moves the caller to
// whatever that path collects next, not a value that stays selected here.
//
// THE REASON IDIOM. Same shape this lane already uses in three other
// places — `RadioGroup`'s `RadioOption.description` (paired with
// `aria-describedby`, "the option stays in the list, greyed and
// unselectable, with the reason both visible and announced"),
// `StatusStepper`'s `step.reason`, and `PinnableOrderList`'s
// `disabledReason` — never a new pattern. An unavailable option renders
// IN PLACE, real `disabled` on the control, with a visible sentence saying
// why, wired to the control via `aria-describedby`. It never disappears and
// it is never a dead click target that does nothing.

export interface OptionCardProps {
  /** Heroicon (or any 24x24-viewBox SVG component) rendered beside the title. */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  onSelect: () => void;
  /** Renders the card unavailable — real `disabled`, not a styled-only state. */
  disabled?: boolean;
  /** Required alongside `disabled`: the visible, announced reason why. */
  reason?: string;
  className?: string;
  'data-testid'?: string;
}

export function OptionCard({
  icon: Icon,
  title,
  description,
  onSelect,
  disabled = false,
  reason,
  className = '',
  'data-testid': testId,
}: OptionCardProps): JSX.Element {
  const showReason = disabled && Boolean(reason);
  const reasonId = showReason ? `${testId ?? title.replace(/\s+/g, '-').toLowerCase()}-reason` : undefined;

  return (
    <Button
      type="button"
      variant="secondary"
      className={`h-full w-full flex-col items-start gap-1 p-4 text-left ${className}`}
      onClick={onSelect}
      disabled={disabled}
      {...(reasonId ? { 'aria-describedby': reasonId } : {})}
      data-testid={testId}
    >
      <span className="flex items-center gap-2 font-medium text-foreground">
        <Icon className="h-4 w-4" aria-hidden="true" />
        {title}
      </span>
      <span className="whitespace-normal text-xs text-muted-foreground">{description}</span>
      {showReason && (
        <span id={reasonId} className="whitespace-normal text-xs font-medium text-warning" data-testid={testId ? `${testId}-reason` : undefined}>
          {reason}
        </span>
      )}
    </Button>
  );
}
